// ExpenseManagement.jsx
import React, {useCallback, useEffect, useState, useRef} from "react";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Container,
    Stack,
    Chip,
    Divider,
    LinearProgress,
    Snackbar,
    Alert,
    Collapse,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Paper,
    IconButton
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";


import {
    getExpenseMonths,
    getExpensesForMonth,
    getExpenseById,
    uploadExpense,
    updateExpense,
    parseExpense,
    createExpense,
    getExpenseReceipt,
    processExpenseReceipt,
    uploadExpenseReceipt,
    createSupplier,
    createAsset,
    getRecurringTransactions,
    createRecurringTransaction,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    getSuppliers,
} from "../../api";
import {logError, logInfo, logWarn} from "../../logging/loggingService";
import {useAuth} from "../auth/AuthContext";
import {hasAccountingAccess} from "../../subscription";
import UpgradeNotice from "../shared/UpgradeNotice";
import AssetFormDialog from "../assets/AssetFormDialog";
import ContextTooltip from "../shared/ContextTooltip";
import AppDatePicker from "../shared/AppDatePicker";
import {
    buildSupplierOptions,
    SUPPLIER_SELECT_EMPTY_HELPER,
    SUPPLIER_SELECT_LABEL,
    SUPPLIER_SELECT_LOADING,
    SUPPLIER_SELECT_NO_OPTIONS,
    SUPPLIER_SELECT_PLACEHOLDER,
    findMatchingSupplierOption,
    getSupplierOptionLabel,
    isSupplierOptionEqualToValue,
    resolveSupplierOption,
} from "../shared/supplierSelectUtils";
import MultilineTextField from "../shared/MultilineTextField";
import RecurringExpenseSection from "./expense-management/RecurringExpenseSection";
import ExpenseList from "./expense-management/ExpenseList";
import {
    applyEuCalculation,
    buildMonthKey,
    emptyToNull,
    EU_DEFAULT_TAX_RATE,
    extractTaxRate,
    extOf,
    formatBytes,
    fmtEUR,
    expenseGroupStateKey,
    getDefaultExpenseGroupState,
    hasValue,
    isReceiptProcessingActive,
    normalizeExpenseMonths,
    normalizeReceiptProcessingStatus,
    parseDayOfMonth,
    parseNumberInput,
    readExpenseGroupState,
    RECEIPT_PROCESSING_STATUS,
    resolveExpenseDisplayName,
    resolveExpenseMonthInfo,
    sortExpensesByDate,
    toDateInput,
    toEditableNumber,
    UNKNOWN_MONTH_GROUP_KEY,
    UNKNOWN_MONTH_KEY,
    UNKNOWN_YEAR_KEY,
} from "./expense-management/expenseManagementUtils";

const recurrenceOptions = [
    {value: "MONTHLY", label: "Monatlich"},
    {value: "QUARTERLY", label: "Quartalsweise"},
    {value: "YEARLY", label: "Jährlich"},
];

const ASSET_SUGGESTION_THRESHOLD = 800;

const EXPENSE_CATEGORIES = [
    "Waren/Wareneinkauf",
    "Fremdleistungen/Dienstleistungen",
    "Büro & Verwaltung",
    "Telefon/Internet/IT",
    "Miete, Pacht & Nebenkosten",
    "Kfz & Mobilität",
    "Reisekosten",
    "Bewirtung (70% abziehbar)",
    "Bewirtung (30% nicht abziehbar)",
    "Marketing & Werbung",
    "Versicherungen & Beiträge",
    "Reparatur & Wartung",
    "Fortbildung & Seminare",
    "Beratung & Buchhaltung",
    "Bank & Finanzen",
    "GWG",
    "Anlagen/Investitionen",
    "Sonstiges",
];

export default function ExpenseManagement() {
    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({open: false, msg: "", sev: "success"});
    const [expenseErrors, setExpenseErrors] = useState({});
    const [detailErrors, setDetailErrors] = useState({});
    const [recurringErrors, setRecurringErrors] = useState({});

    const [expenseMonthIndex, setExpenseMonthIndex] = useState([]);
    const [expenseMonthIndexLoaded, setExpenseMonthIndexLoaded] = useState(false);
    const [expenseMonthData, setExpenseMonthData] = useState({});
    const [loadingExpenseMonths, setLoadingExpenseMonths] = useState(() => new Set());
    const [loadedExpenseMonths, setLoadedExpenseMonths] = useState(() => new Set());
    const [expandedExpenseGroups, setExpandedExpenseGroups] = useState(() => {
        const stored = readExpenseGroupState();
        return stored ?? getDefaultExpenseGroupState();
    });

    // Dialog-/Form-States
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [expenseData, setExpenseData] = useState(null);
    const updateExpenseData = useCallback((patch) => {
        setExpenseData((prev) => {
            const base = prev || {taxExcludedWithdrawal: false, isEuService: false};
            const resolved = typeof patch === "function" ? patch(base) : patch;
            return {...base, ...resolved};
        });
    }, []);
    const updateExpenseDataWithEu = useCallback((patch, changedField) => {
        setExpenseData((prev) => {
            const base = prev || {taxExcludedWithdrawal: false, isEuService: false};
            const resolved = typeof patch === "function" ? patch(base) : patch;
            return applyEuCalculation(base, resolved, changedField, (value) => value);
        });
    }, []);

    const clearFieldError = useCallback((setter, field) => {
        setter((prev) => {
            if (!prev || !prev[field]) return prev;
            const next = {...prev};
            delete next[field];
            return next;
        });
    }, []);

    const clearExpenseError = useCallback((field) => clearFieldError(setExpenseErrors, field), [clearFieldError]);
    const clearDetailError = useCallback((field) => clearFieldError(setDetailErrors, field), [clearFieldError]);
    const clearRecurringError = useCallback((field) => clearFieldError(setRecurringErrors, field), [clearFieldError]);

    const applyExpenseErrors = (details, setter) => {
        if (!Array.isArray(details) || details.length === 0) return false;
        const next = {};
        details.forEach((detail) => {
            const rawField = detail?.field;
            const message = detail?.message;
            if (!rawField || !message) return;
            const normalizedField = rawField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            let field = normalizedField;
            if (field === "supplierName") {
                field = "supplierId";
            }
            if (field === "vatRate") {
                field = "vatRates";
            }
            next[field] = message;
        });
        if (Object.keys(next).length === 0) return false;
        setter(next);
        return true;
    };

    const applyRecurringErrors = (details) => {
        if (!Array.isArray(details) || details.length === 0) return false;
        const next = {};
        details.forEach((detail) => {
            const rawField = detail?.field;
            const message = detail?.message;
            if (!rawField || !message) return;
            const normalizedField = rawField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            let field = normalizedField;
            if (field === "customerName" || field === "supplierName") {
                field = "counterparty";
            }
            next[field] = message;
        });
        if (Object.keys(next).length === 0) return false;
        setRecurringErrors(next);
        return true;
    };
    const [manual, setManual] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailExpense, setDetailExpense] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailSaving, setDetailSaving] = useState(false);
    const [copyingId, setCopyingId] = useState(null);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [receiptExpense, setReceiptExpense] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [receiptError, setReceiptError] = useState(null);
    const [assetDialogOpen, setAssetDialogOpen] = useState(false);
    const [assetDraft, setAssetDraft] = useState(null);
    const [assetSaving, setAssetSaving] = useState(false);
    const [assetError, setAssetError] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const receiptProcessingNotifiedRef = useRef(new Set());
    const receiptProcessingErrorNotifiedRef = useRef(new Set());
    const dropRef = useRef(null);
    const fileInputRef = useRef(null);

    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [recurringLoading, setRecurringLoading] = useState(false);
    const [recurringError, setRecurringError] = useState(null);
    const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
    const [recurringForm, setRecurringForm] = useState(null);
    const [recurringMode, setRecurringMode] = useState("create");
    const [recurringSaving, setRecurringSaving] = useState(false);
    const [recurringDeleteId, setRecurringDeleteId] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [supplierCreating, setSupplierCreating] = useState(false);
    const supplierOptions = React.useMemo(() => buildSupplierOptions(suppliers), [suppliers]);

    const {user} = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);
    const detailGrossPreview = detailData ? parseNumberInput(detailData.grossAmount) : null;
    const fileName = file?.name || "";
    const fileType = file?.type || "";
    const receiptProcessingStatus = normalizeReceiptProcessingStatus(receiptExpense?.receiptProcessingStatus);
    const receiptProcessingError = receiptExpense?.receiptProcessingError || "";
    const detailReceiptProcessingStatus = normalizeReceiptProcessingStatus(detailExpense?.receiptProcessingStatus);
    const detailReceiptProcessingError = detailExpense?.receiptProcessingError || "";
    const isPreviewPdf = Boolean(file) && (fileType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf"));
    const isPreviewImage =
        Boolean(file) &&
        (fileType.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName));
    const receiptFileName = receiptExpense?.originalFilename || "";
    const receiptContentType = String(receiptPreview?.type || "").toLowerCase();
    const isReceiptPdf =
        receiptContentType.includes("pdf") || receiptFileName.toLowerCase().endsWith(".pdf");
    const isReceiptImage =
        receiptContentType.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp)$/i.test(receiptFileName);

    const shouldSuggestAsset = useCallback((expense) => {
        if (!expense) return false;
        if (expense.taxExcludedWithdrawal) {
            return false;
        }
        const amount = parseNumberInput(expense.netAmount ?? expense.grossAmount);
        return Number.isFinite(amount) && amount >= ASSET_SUGGESTION_THRESHOLD;
    }, []);

    /* ===== Load ===== */
    const ensureExpenseMonthIndex = useCallback((year, monthKey) => {
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return;
        setExpenseMonthIndex((prev) => {
            const exists = prev.some((entry) => entry.year === yearNum && entry.month === monthNum);
            if (exists) return prev;
            return [...prev, {year: yearNum, month: monthNum}];
        });
    }, []);

    const upsertExpense = useCallback(
        (nextExpense) => {
            if (!nextExpense?.id) return;
            const monthInfo = resolveExpenseMonthInfo(nextExpense);
            const targetYearKey = monthInfo?.yearKey ?? UNKNOWN_YEAR_KEY;
            const targetMonthKey = monthInfo?.monthKey ?? UNKNOWN_MONTH_KEY;
            const targetGroupKey = buildMonthKey(targetYearKey, targetMonthKey);

            setExpenseMonthData((prev) => {
                const next = {...prev};
                Object.entries(prev).forEach(([key, list]) => {
                    if (key === targetGroupKey || !Array.isArray(list)) return;
                    const filtered = list.filter((item) => item.id !== nextExpense.id);
                    if (filtered.length !== list.length) {
                        next[key] = filtered;
                    }
                });
                const existing = Array.isArray(prev[targetGroupKey]) ? prev[targetGroupKey] : [];
                const index = existing.findIndex((item) => item.id === nextExpense.id);
                let updatedList = [];
                if (index === -1) {
                    updatedList = [nextExpense, ...existing];
                } else {
                    updatedList = [...existing];
                    updatedList[index] = {...updatedList[index], ...nextExpense};
                }
                next[targetGroupKey] = sortExpensesByDate(updatedList);
                return next;
            });

            setLoadedExpenseMonths((prev) => {
                const next = new Set(prev);
                next.add(targetGroupKey);
                return next;
            });

            if (monthInfo) {
                ensureExpenseMonthIndex(monthInfo.year, monthInfo.month);
            }
        },
        [ensureExpenseMonthIndex]
    );

    const buildAssetDraft = useCallback((expense) => {
        if (!expense) return null;
        const amount = parseNumberInput(expense.netAmount ?? expense.grossAmount);
        const acquisitionDate = toDateInput(expense.invoiceDate || expense.uploadDate || new Date());
        const supplierName = expense.supplierName ? expense.supplierName.trim() : "";
        const baseName = expense.invoiceNumber
            ? `Beleg ${expense.invoiceNumber}`
            : supplierName
                ? `Anlage ${supplierName}`
                : "Anlagegut";
        return {
            name: baseName,
            acquisitionDate,
            acquisitionValue: amount ?? "",
            usefulLifeYears: 3,
            method: "LINEAR",
            residualValue: 0,
            invoiceId: expense.id,
            invoiceNumber: expense.invoiceNumber || expense.originalFilename || null,
            invoiceDate: expense.invoiceDate || expense.uploadDate || null,
        };
    }, []);

    const loadSuppliers = useCallback(() => {
        setSuppliersLoading(true);
        return getSuppliers()
            .then((res) => setSuppliers(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                logError("Fehler beim Laden der Lieferanten", err, {component: "ExpenseManagement"});
            })
            .finally(() => setSuppliersLoading(false));
    }, []);

    const handleSuggestAsset = useCallback(
        (expense) => {
            const draft = buildAssetDraft(expense);
            setAssetDraft(draft);
            setAssetError(null);
            setAssetDialogOpen(Boolean(draft));
        },
        [buildAssetDraft]
    );

    const handleCloseAssetDialog = useCallback(() => {
        if (assetSaving) return;
        setAssetDialogOpen(false);
        setAssetDraft(null);
        setAssetError(null);
    }, [assetSaving]);

    const handleSaveAsset = useCallback(
        async (payload) => {
            setAssetSaving(true);
            setAssetError(null);
            try {
                await createAsset(payload);
                setSnack({open: true, msg: "Anlagegut angelegt", sev: "success"});
                setAssetDialogOpen(false);
                setAssetDraft(null);
            } catch (err) {
                logError("Fehler beim Anlegen des Anlageguts", err, {component: "ExpenseManagement"});
                setAssetError(err?.message || "Anlagegut konnte nicht gespeichert werden.");
            } finally {
                setAssetSaving(false);
            }
        },
        [setSnack]
    );

    const findSupplierById = useCallback(
        (id) => (id ? suppliers.find((supplier) => supplier.id === id) || null : null),
        [suppliers]
    );

    const buildSupplierValue = useCallback(
        (id, name) => {
            const supplier = findSupplierById(id);
            if (supplier) {
                return supplier;
            }
            const matched = findMatchingSupplierOption(supplierOptions, name);
            if (!matched) {
                return null;
            }
            return findSupplierById(matched.id);
        },
        [findSupplierById, supplierOptions]
    );

    const formatSupplierAddress = useCallback((supplier) => {
        if (!supplier) return "";
        const pieces = [];
        if (supplier.street && supplier.street.trim()) {
            pieces.push(supplier.street.trim());
        }
        const cityLine = [supplier.zip, supplier.city]
            .filter((part) => part && String(part).trim())
            .map((part) => String(part).trim())
            .join(" ")
            .trim();
        if (cityLine) {
            pieces.push(cityLine);
        }
        if (supplier.country && supplier.country.trim()) {
            pieces.push(supplier.country.trim());
        }
        return pieces.join(", ");
    }, []);

    const parseSupplierAddress = useCallback((value) => {
        if (!value || !value.trim()) {
            return {};
        }
        const parts = value
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);
        const [street, zipCity, country] = parts;
        let zip = "";
        let city = "";
        if (zipCity) {
            const match = zipCity.match(/^(\d{4,6})\s+(.+)$/);
            if (match) {
                zip = match[1];
                city = match[2];
            } else {
                city = zipCity;
            }
        }
        return {
            street: street || "",
            zip,
            city,
            country: country || "",
        };
    }, []);

    const buildSupplierDraft = useCallback(
        (name, address) => {
            const trimmedName = name ? name.trim() : "";
            if (!trimmedName) {
                return null;
            }
            return {
                name: trimmedName,
                ...parseSupplierAddress(address),
            };
        },
        [parseSupplierAddress]
    );

    const handleExpenseSupplierSelection = (value) => {
        if (!value) {
            updateExpenseData({supplierName: "", supplierId: null});
            clearExpenseError("supplierId");
            return;
        }
        const selectedSupplier = findSupplierById(value.id);
        updateExpenseData((current) => {
            const patch = {
                supplierId: value.id || null,
                supplierName: value.displayName || value.label || "",
            };
            if (selectedSupplier && !(current.supplierAddress && current.supplierAddress.trim())) {
                const address = formatSupplierAddress(selectedSupplier);
                if (address) {
                    patch.supplierAddress = address;
                }
            }
            return patch;
        });
        clearExpenseError("supplierId");
    };

    const handleExpenseSupplierInput = (value, reason) => {
        if (reason === "clear") {
            updateExpenseData({supplierName: "", supplierId: null});
            clearExpenseError("supplierId");
            return;
        }
        if (reason === "input") {
            updateExpenseData({supplierName: value, supplierId: null});
            clearExpenseError("supplierId");
        }
    };

    const handleDetailSupplierSelection = (value) => {
        if (!value) {
            setDetailData((prev) =>
                prev ? {...prev, supplierName: "", supplierId: null} : prev
            );
            clearDetailError("supplierId");
            return;
        }
        const selectedSupplier = findSupplierById(value.id);
        setDetailData((prev) => {
            if (!prev) {
                return prev;
            }
            const patch = {
                supplierId: value.id || null,
                supplierName: value.displayName || value.label || "",
            };
            if (selectedSupplier && !(prev.supplierAddress && prev.supplierAddress.trim())) {
                const address = formatSupplierAddress(selectedSupplier);
                if (address) {
                    patch.supplierAddress = address;
                }
            }
            return {...prev, ...patch};
        });
        clearDetailError("supplierId");
    };

    const handleDetailSupplierInput = (value, reason) => {
        if (reason === "clear") {
            setDetailData((prev) =>
                prev ? {...prev, supplierName: "", supplierId: null} : prev
            );
            clearDetailError("supplierId");
            return;
        }
        if (reason === "input") {
            setDetailData((prev) =>
                prev ? {...prev, supplierName: value, supplierId: null} : prev
            );
            clearDetailError("supplierId");
        }
    };

    const handleCreateSupplierFromExpense = useCallback(async () => {
        if (!expenseData) return;
        const draft = buildSupplierDraft(expenseData.supplierName, expenseData.supplierAddress);
        if (!draft) return;
        try {
            setSupplierCreating(true);
            const res = await createSupplier(draft);
            const created = res.data;
            setSuppliers((prev) =>
                [...prev, created].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
            );
            const createdAddress = formatSupplierAddress(created);
            updateExpenseData({
                supplierId: created?.id ?? null,
                supplierName: created?.name ?? draft.name,
                supplierAddress: createdAddress || expenseData.supplierAddress || "",
            });
            setSnack({open: true, msg: "Lieferant angelegt", sev: "success"});
        } catch (err) {
            logError("Fehler beim Anlegen des Lieferanten", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Lieferant konnte nicht angelegt werden", sev: "error"});
        } finally {
            setSupplierCreating(false);
        }
    }, [buildSupplierDraft, expenseData, formatSupplierAddress, updateExpenseData]);

    const handleCreateSupplierFromDetail = useCallback(async () => {
        if (!detailData) return;
        const draft = buildSupplierDraft(detailData.supplierName, detailData.supplierAddress);
        if (!draft) return;
        try {
            setSupplierCreating(true);
            const res = await createSupplier(draft);
            const created = res.data;
            setSuppliers((prev) =>
                [...prev, created].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
            );
            const createdAddress = formatSupplierAddress(created);
            setDetailData((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    supplierId: created?.id ?? null,
                    supplierName: created?.name ?? draft.name,
                    supplierAddress: createdAddress || prev.supplierAddress || "",
                };
            });
            setSnack({open: true, msg: "Lieferant angelegt", sev: "success"});
        } catch (err) {
            logError("Fehler beim Anlegen des Lieferanten", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Lieferant konnte nicht angelegt werden", sev: "error"});
        } finally {
            setSupplierCreating(false);
        }
    }, [buildSupplierDraft, detailData, formatSupplierAddress]);


    const loadExpenseMonthIndex = useCallback(async (options = {}) => {
        if (expenseMonthIndexLoaded && !options.force) return expenseMonthIndex;
        try {
            const res = await getExpenseMonths();
            const normalized = normalizeExpenseMonths(res?.data);
            logInfo("Ausgabenmonate geladen", {
                component: "ExpenseManagement",
                returnedEntries: Array.isArray(res?.data) ? res.data.length : 0,
                normalizedMonths: normalized.length,
                force: Boolean(options.force),
            });
            if (Array.isArray(res?.data) && res.data.length > 0 && normalized.length === 0) {
                logWarn("Ausgabenmonate konnten nicht normalisiert werden", {
                    component: "ExpenseManagement",
                    responseSample: res.data.slice(0, 3),
                });
            }
            setExpenseMonthIndex(normalized);
            return normalized;
        } catch (err) {
            logError("Fehler beim Laden der Ausgabenmonate", err, {component: "ExpenseManagement"});
            setExpenseMonthIndex([]);
            return [];
        } finally {
            setExpenseMonthIndexLoaded(true);
        }
    }, [expenseMonthIndex, expenseMonthIndexLoaded]);

    const loadExpensesForMonthKey = useCallback(async (year, monthKey, options = {}) => {
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return [];
        const key = buildMonthKey(String(yearNum), String(monthNum));
        if (loadingExpenseMonths.has(key)) return [];
        if (!options.force && loadedExpenseMonths.has(key)) return expenseMonthData[key] || [];

        setLoadingExpenseMonths((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });

        try {
            const res = await getExpensesForMonth(yearNum, monthNum);
            const data = sortExpensesByDate(Array.isArray(res?.data) ? res.data : []);
            logInfo("Ausgabenmonat geladen", {
                component: "ExpenseManagement",
                year: yearNum,
                month: monthNum + 1,
                returnedExpenses: data.length,
                force: Boolean(options.force),
            });
            setExpenseMonthData((prev) => ({...prev, [key]: data}));
            setLoadedExpenseMonths((prev) => {
                const next = new Set(prev);
                next.add(key);
                return next;
            });
            ensureExpenseMonthIndex(yearNum, monthNum);
            return data;
        } catch (err) {
            logError("Fehler beim Laden der Ausgaben", err, {component: "ExpenseManagement"});
            return [];
        } finally {
            setLoadingExpenseMonths((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    }, [loadingExpenseMonths, loadedExpenseMonths, expenseMonthData, ensureExpenseMonthIndex]);

    const expandedGroupsRef = useRef(expandedExpenseGroups);
    useEffect(() => {
        expandedGroupsRef.current = expandedExpenseGroups;
    }, [expandedExpenseGroups]);

    const loadedMonthsRef = useRef(loadedExpenseMonths);
    useEffect(() => {
        loadedMonthsRef.current = loadedExpenseMonths;
    }, [loadedExpenseMonths]);

    const refresh = useCallback(async (options = {}) => {
        const {silent = false} = options;
        if (!silent) {
            setLoading(true);
        }
        try {
            await loadExpenseMonthIndex({force: true});

            const expandedKeys = Object.entries(expandedGroupsRef.current?.months || {})
                .filter(([, isExpanded]) => isExpanded)
                .map(([key]) => key);

            const monthsToLoad = new Set([
                ...Array.from(loadedMonthsRef.current),
                ...expandedKeys,
            ]);

            const tasks = Array.from(monthsToLoad)
                .map((key) => {
                    const [year, monthKey] = key.split("-");
                    if (!Number.isFinite(Number(year)) || !Number.isFinite(Number(monthKey))) return null;
                    return loadExpensesForMonthKey(year, monthKey, {force: true});
                })
                .filter(Boolean);

            await Promise.all(tasks);
        } catch (err) {
            logError("Fehler beim Aktualisieren der Ausgaben", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Fehler beim Aktualisieren der Ausgaben", sev: "error"});
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [loadExpenseMonthIndex, loadExpensesForMonthKey]);

    const toggleYearExpanded = (year) => {
        setExpandedExpenseGroups((prev) => {
            const years = {...(prev?.years || {})};
            years[year] = !years[year];
            return {years, months: prev?.months || {}};
        });
    };

    const toggleMonthExpanded = (year, monthKey) => {
        const key = buildMonthKey(year, monthKey);
        const isExpanded = !!expandedExpenseGroups?.months?.[key];
        setExpandedExpenseGroups((prev) => {
            const months = {...(prev?.months || {})};
            months[key] = !months[key];
            return {years: prev?.years || {}, months};
        });
        if (!isExpanded) {
            loadExpensesForMonthKey(year, monthKey);
        }
    };

    const groupedExpenseMonths = React.useMemo(() => {
        const groups = {};
        if (expenseMonthIndexLoaded) {
            const months = Array.isArray(expenseMonthIndex) ? expenseMonthIndex : [];
            months.forEach(({year, month}) => {
                const yearKey = String(year);
                const monthKey = String(month);
                if (!groups[yearKey]) groups[yearKey] = [];
                if (!groups[yearKey].includes(monthKey)) {
                    groups[yearKey].push(monthKey);
                }
            });
        }

        if (Array.isArray(expenseMonthData[UNKNOWN_MONTH_GROUP_KEY]) && expenseMonthData[UNKNOWN_MONTH_GROUP_KEY].length > 0) {
            if (!groups[UNKNOWN_YEAR_KEY]) groups[UNKNOWN_YEAR_KEY] = [];
            if (!groups[UNKNOWN_YEAR_KEY].includes(UNKNOWN_MONTH_KEY)) {
                groups[UNKNOWN_YEAR_KEY].push(UNKNOWN_MONTH_KEY);
            }
        }

        Object.keys(groups).forEach((yearKey) => {
            groups[yearKey].sort((a, b) => {
                if (a === UNKNOWN_MONTH_KEY) return 1;
                if (b === UNKNOWN_MONTH_KEY) return -1;
                return Number(b) - Number(a);
            });
        });

        return groups;
    }, [expenseMonthData, expenseMonthIndex, expenseMonthIndexLoaded]);

    const sortedYears = React.useMemo(() => {
        return Object.keys(groupedExpenseMonths).sort((a, b) => {
            if (a === UNKNOWN_YEAR_KEY) return 1;
            if (b === UNKNOWN_YEAR_KEY) return -1;
            return Number(b) - Number(a);
        });
    }, [groupedExpenseMonths]);

    const hasUnknownExpenses =
        Array.isArray(expenseMonthData[UNKNOWN_MONTH_GROUP_KEY]) &&
        expenseMonthData[UNKNOWN_MONTH_GROUP_KEY].length > 0;
    const hasExpenseMonths = expenseMonthIndexLoaded && expenseMonthIndex.length > 0;

    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            sessionStorage.setItem(expenseGroupStateKey, JSON.stringify(expandedExpenseGroups));
        } catch {
            // ignore
        }
    }, [expandedExpenseGroups]);

    const refreshRecurring = useCallback(() => {
        setRecurringLoading(true);
        setRecurringError(null);
        return getRecurringTransactions("EXPENSE")
            .then((res) => setRecurringExpenses(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                logError("Fehler beim Laden der wiederkehrenden Ausgaben", err, {component: "ExpenseManagement"});
                setRecurringError("Wiederkehrende Ausgaben konnten nicht geladen werden.");
            })
            .finally(() => setRecurringLoading(false));
    }, []);

    useEffect(() => {
        if (!hasAccounting) {
            setLoading(false);
            setRecurringLoading(false);
            setRecurringExpenses([]);
            setRecurringError(null);
            setSuppliers([]);
            setSuppliersLoading(false);
            return;
        }
        refresh();
        refreshRecurring();
        loadSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccounting]);

    useEffect(() => {
        if (!hasAccounting) return;
        const el = dropRef.current;
        if (!el || manual) return;
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const onDrop = (e) => {
            prevent(e);
            const f = e.dataTransfer?.files?.[0];
            if (f) setFile(f);
        };
        const onOver = (e) => {
            prevent(e);
            el.classList.add("drag-active");
        };
        const onLeave = (e) => {
            prevent(e);
            el.classList.remove("drag-active");
        };
        el.addEventListener("dragenter", onOver);
        el.addEventListener("dragover", onOver);
        el.addEventListener("dragleave", onLeave);
        el.addEventListener("drop", onDrop);
        return () => {
            el.removeEventListener("dragenter", onOver);
            el.removeEventListener("dragover", onOver);
            el.removeEventListener("dragleave", onLeave);
            el.removeEventListener("drop", onDrop);
        };
    }, [open, manual, hasAccounting]);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            setPreviewVisible(false);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    useEffect(() => {
        if (!expenseData || expenseData.supplierId || !expenseData.supplierName || suppliers.length === 0) {
            return;
        }
        const match = buildSupplierValue(null, expenseData.supplierName);
        if (match) {
            const address = formatSupplierAddress(match);
            updateExpenseData({
                supplierId: match.id ?? null,
                supplierName: match.name ?? expenseData.supplierName,
                supplierAddress: address || expenseData.supplierAddress || "",
            });
        }
    }, [
        buildSupplierValue,
        expenseData,
        formatSupplierAddress,
        suppliers,
        updateExpenseData,
    ]);

    useEffect(() => {
        if (!detailData || detailData.supplierId || !detailData.supplierName || suppliers.length === 0) {
            return;
        }
        const match = buildSupplierValue(null, detailData.supplierName);
        if (match) {
            const address = formatSupplierAddress(match);
            setDetailData((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    supplierId: match.id ?? null,
                    supplierName: match.name ?? prev.supplierName,
                    supplierAddress: address || prev.supplierAddress || "",
                };
            });
        }
    }, [buildSupplierValue, detailData, formatSupplierAddress, suppliers]);

    /* ===== Actions ===== */
    const handleOpenDialog = () => {
        setOpen(true);
        setFile(null);
        setExpenseData(null);
        setManual(false);
        setExpenseErrors({});
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setFile(null);
        setExpenseData(null);
        setManual(false);
        setParsing(false);
        setSaving(false);
        setPreviewVisible(false);
        setExpenseErrors({});
    };

    const handleOpenDetails = (expense) => {
        if (!expense) return;
        setReceiptFile(null);
        setDetailExpense(expense);

        let initialDetailData = {
            invoiceDate: toDateInput(expense.invoiceDate),
            paymentDueDate: toDateInput(expense.paymentDueDate),
            invoiceNumber: expense.invoiceNumber ?? "",
            paymentMethod: expense.paymentMethod ?? "",
            supplierId: expense.supplierId ?? null,
            supplierName: expense.supplierName ?? "",
            supplierAddress: expense.supplierAddress ?? "",
            iban: expense.iban ?? "",
            grossAmount: toEditableNumber(expense.grossAmount),
            netAmount: toEditableNumber(expense.netAmount),
            vatAmount: toEditableNumber(expense.vatAmount),
            vatRates: expense.vatRates ?? "",
            servicePeriod: expense.servicePeriod ?? "",
            positionsData: expense.positionsData ?? "",
            invoiceType: expense.invoiceType ?? "",
            category: expense.category ?? "",
            taxExcludedWithdrawal: Boolean(expense.taxExcludedWithdrawal),
            isEuService: Boolean(expense.isEuService),
        };

        if (initialDetailData.isEuService && !hasValue(initialDetailData.vatRates)) {
            initialDetailData.vatRates = EU_DEFAULT_TAX_RATE;
        }

        const calculatedDetailData = applyEuCalculation(
            initialDetailData,
            {},
            "vatRates",
            toEditableNumber
        );

        setDetailData(calculatedDetailData);
        setDetailErrors({});
        setDetailOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailOpen(false);
        setDetailExpense(null);
        setDetailData(null);
        setDetailSaving(false);
        setReceiptFile(null);
        setDetailErrors({});
    };

    const updateDetail = (field, value) => {
        setDetailData((prev) => ({...(prev || {}), [field]: value}));
        clearDetailError(field);
    };
    const updateDetailDataWithEu = useCallback((patch, changedField) => {
        setDetailData((prev) => {
            if (!prev) {
                return prev;
            }
            const resolved = typeof patch === "function" ? patch(prev) : patch;
            return applyEuCalculation(prev, resolved, changedField, toEditableNumber);
        });
        if (changedField) {
            clearDetailError(changedField);
            if (changedField === "vatRates" || changedField === "grossAmount" || changedField === "vatAmount") {
                clearDetailError("netAmount");
            }
        }
    }, [clearDetailError]);

    const validateEuService = (data, setErrors) => {
        if (!data?.isEuService) {
            return true;
        }
        const rate = extractTaxRate(data.vatRates);
        if (!Number.isFinite(rate)) {
            if (setErrors) {
                setErrors((prev) => ({
                    ...(prev || {}),
                    vatRates: "Bitte Steuersatz fuer EU-Leistung angeben.",
                }));
            }
            setSnack({open: true, msg: "Bitte Steuersatz fuer EU-Leistung angeben.", sev: "error"});
            return false;
        }
        const net = parseNumberInput(data.netAmount);
        if (!Number.isFinite(net) || net === 0) {
            if (setErrors) {
                setErrors((prev) => ({
                    ...(prev || {}),
                    netAmount: "Nettobetrag darf bei EU-Leistung nicht 0 sein.",
                }));
            }
            setSnack({open: true, msg: "Nettobetrag fuer EU-Leistung darf nicht 0 sein.", sev: "error"});
            return false;
        }
        return true;
    };

    const handleDetailSave = async () => {
        if (!detailExpense?.id || !detailData) return;
        setDetailErrors({});
        if (!validateEuService(detailData, setDetailErrors)) return;
        try {
            setDetailSaving(true);
            const payload = {
                invoiceDate: detailData.invoiceDate || null,
                paymentDueDate: detailData.paymentDueDate || null,
                invoiceNumber: detailData.invoiceNumber || null,
                paymentMethod: detailData.paymentMethod || null,
                supplierId: detailData.supplierId || null,
                supplierName: detailData.supplierName || null,
                supplierAddress: detailData.supplierAddress || null,
                iban: detailData.iban || null,
                grossAmount: parseNumberInput(detailData.grossAmount),
                netAmount: parseNumberInput(detailData.netAmount),
                vatAmount: parseNumberInput(detailData.vatAmount),
                vatRates: detailData.vatRates || null,
                servicePeriod: detailData.servicePeriod || null,
                positionsData: detailData.positionsData || null,
                invoiceType: detailData.invoiceType || null,
                category: detailData.category || null,
                taxExcludedWithdrawal: Boolean(detailData.taxExcludedWithdrawal),
                isEuService: Boolean(detailData.isEuService),
            };
            const response = await updateExpense(detailExpense.id, payload);
            let savedExpense = {...detailExpense, ...(response?.data || {})};
            if (receiptFile) {
                try {
                    const formData = new FormData();
                    formData.append("file", receiptFile);
                    const receiptResponse = await uploadExpenseReceipt(detailExpense.id, formData);
                    savedExpense = {...savedExpense, ...(receiptResponse?.data || {})};
                } catch (err) {
                    logError("Fehler beim Hochladen des neuen Belegs", err, {component: "ExpenseManagement"});
                    setSnack({open: true, msg: "Der neue Beleg konnte nicht hochgeladen werden.", sev: "error"});
                }
            }
            upsertExpense(savedExpense);
            refresh({silent: true});
            setSnack({open: true, msg: "Ausgabe aktualisiert", sev: "success"});
            handleCloseDetails();
        } catch (err) {
            logError("Fehler beim Aktualisieren der Ausgabe", err, {component: "ExpenseManagement"});
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyExpenseErrors(details, setDetailErrors);
            }
            setSnack({open: true, msg: "Fehler beim Aktualisieren der Ausgabe", sev: "error"});
        } finally {
            setDetailSaving(false);
        }
    };

    const handleCopyExpense = async (expense) => {
        if (!expense) return;
        const payload = {
            originalFilename: null,
            invoiceNumber: expense.invoiceNumber ?? null,
            invoiceDate: null,
            supplierId: expense.supplierId ?? null,
            supplierName: expense.supplierName ?? null,
            supplierAddress: expense.supplierAddress ?? null,
            grossAmount: parseNumberInput(expense.grossAmount),
            netAmount: parseNumberInput(expense.netAmount),
            vatAmount: parseNumberInput(expense.vatAmount),
            vatRates: expense.vatRates ?? null,
            paymentDueDate: null,
            paymentMethod: expense.paymentMethod ?? null,
            iban: expense.iban ?? null,
            servicePeriod: null,
            positionsData: expense.positionsData ?? null,
            invoiceType: expense.invoiceType ?? null,
            category: expense.category ?? null,
            taxExcludedWithdrawal: Boolean(expense.taxExcludedWithdrawal),
            isEuService: Boolean(expense.isEuService),
        };
        setCopyingId(expense.id ?? null);
        try {
            const response = await createExpense(payload);
            upsertExpense(response?.data);
            setSnack({open: true, msg: "Ausgabe kopiert", sev: "success"});
            if (response?.data) {
                handleOpenDetails(response.data);
            }
        } catch (err) {
            logError("Fehler beim Kopieren der Ausgabe", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Fehler beim Kopieren der Ausgabe", sev: "error"});
        } finally {
            setCopyingId(null);
        }
    };

    const hasReceipt = (expense) => {
        if (!expense) return false;
        if (typeof expense.receiptAvailable === "boolean") {
            return expense.receiptAvailable;
        }
        return Boolean(expense.originalFilename);
    };

    const closeReceipt = () => {
        setReceiptOpen(false);
        setReceiptExpense(null);
        setReceiptPreview(null);
        setReceiptError(null);
        setReceiptLoading(false);
    };

    const updateReceiptState = useCallback((updated) => {
        if (!updated?.id) return;
        setReceiptExpense((prev) => (prev?.id === updated.id ? {...prev, ...updated} : prev));
        setDetailExpense((prev) => (prev?.id === updated.id ? {...prev, ...updated} : prev));
    }, []);

    const startReceiptProcessing = useCallback(
        async (expenseId, options = {}) => {
            if (!expenseId) return;
            try {
                const res = await processExpenseReceipt(expenseId, options);
                if (res?.data) {
                    updateReceiptState(res.data);
                }
                const status = normalizeReceiptProcessingStatus(res?.data?.receiptProcessingStatus);
                if (status === RECEIPT_PROCESSING_STATUS.FAILED) {
                    const message = res?.data?.receiptProcessingError
                        ? `Beleg konnte nicht verarbeitet werden: ${res.data.receiptProcessingError}`
                        : "Beleg konnte nicht verarbeitet werden.";
                    if (!receiptProcessingErrorNotifiedRef.current.has(expenseId)) {
                        receiptProcessingErrorNotifiedRef.current.add(expenseId);
                        setSnack({open: true, msg: message, sev: "warning"});
                    }
                } else if (
                    status === RECEIPT_PROCESSING_STATUS.PENDING ||
                    status === RECEIPT_PROCESSING_STATUS.PROCESSING
                ) {
                    if (!receiptProcessingNotifiedRef.current.has(expenseId)) {
                        receiptProcessingNotifiedRef.current.add(expenseId);
                        setSnack({open: true, msg: "Beleg wird im Hintergrund verarbeitet.", sev: "info"});
                    }
                }
            } catch (err) {
                logError("Belegverarbeitung konnte nicht gestartet werden", err, {component: "ExpenseManagement"});
                setSnack({open: true, msg: "Belegverarbeitung konnte nicht gestartet werden.", sev: "error"});
            }
        },
        [setSnack, updateReceiptState]
    );

    const openReceipt = async (expense) => {
        if (!expense?.id) return;
        setReceiptOpen(true);
        setReceiptExpense(expense);
        setReceiptError(null);
        setReceiptLoading(true);
        setReceiptPreview(null);
        const receiptStatus = normalizeReceiptProcessingStatus(expense.receiptProcessingStatus);
        if (
            receiptStatus === RECEIPT_PROCESSING_STATUS.PENDING ||
            receiptStatus === RECEIPT_PROCESSING_STATUS.FAILED
        ) {
            startReceiptProcessing(expense.id, {force: receiptStatus === RECEIPT_PROCESSING_STATUS.FAILED});
        }
        try {
            const res = await getExpenseReceipt(expense.id);
            const blob = res.data;
            if (!blob || blob.size === 0) {
                throw new Error("Beleg ist leer");
            }
            const url = URL.createObjectURL(blob);
            setReceiptPreview({url, type: blob.type || "", size: blob.size});
        } catch (err) {
            logError("Beleg laden fehlgeschlagen", err, {component: "ExpenseManagement"});
            setReceiptError("Beleg konnte nicht geladen werden.");
            setSnack({open: true, msg: "Beleg konnte nicht geladen werden", sev: "error"});
        } finally {
            setReceiptLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (receiptPreview?.url) {
                URL.revokeObjectURL(receiptPreview.url);
            }
        };
    }, [receiptPreview]);

    useEffect(() => {
        if (!receiptOpen || !receiptExpense?.id) return;
        const currentStatus = normalizeReceiptProcessingStatus(receiptExpense.receiptProcessingStatus);
        if (!isReceiptProcessingActive(currentStatus)) return;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 10;
        const intervalMs = 3000;

        const poll = async () => {
            attempts += 1;
            try {
                const res = await getExpenseById(receiptExpense.id);
                const updated = res?.data;
                if (cancelled || !updated) return;
                updateReceiptState(updated);
                const updatedStatus = normalizeReceiptProcessingStatus(updated.receiptProcessingStatus);
                if (
                    updatedStatus === RECEIPT_PROCESSING_STATUS.FAILED &&
                    !receiptProcessingErrorNotifiedRef.current.has(updated.id)
                ) {
                    receiptProcessingErrorNotifiedRef.current.add(updated.id);
                    const message = updated.receiptProcessingError
                        ? `Beleg konnte nicht verarbeitet werden: ${updated.receiptProcessingError}`
                        : "Beleg konnte nicht verarbeitet werden.";
                    setSnack({open: true, msg: message, sev: "warning"});
                }
                if (!isReceiptProcessingActive(updatedStatus) || attempts >= maxAttempts) {
                    clearInterval(timer);
                }
            } catch (err) {
                if (!cancelled) {
                    logError("Belegverarbeitung Statusabruf fehlgeschlagen", err, {component: "ExpenseManagement"});
                }
            }
        };

        const timer = setInterval(poll, intervalMs);
        poll();

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [receiptOpen, receiptExpense?.id, receiptExpense?.receiptProcessingStatus, setSnack, updateReceiptState]);

    const handleUploadParse = async () => {
        if (!file) return;
        const fileDisplayName = file?.name ? file.name.trim() : "";
        try {
            setParsing(true);
            setExpenseErrors({});
            const formData = new FormData();
            formData.append("file", file);
            const res = await parseExpense(formData);
            let parsedData = res.data || {};

            if (!hasValue(parsedData.invoiceNumber) && hasValue(fileDisplayName)) {
                parsedData = {...parsedData, invoiceNumber: fileDisplayName};
            }

            parsedData = {
                ...parsedData,
                taxExcludedWithdrawal: Boolean(parsedData.taxExcludedWithdrawal),
                isEuService: Boolean(parsedData.isEuService),
            };

            if (parsedData.isEuService && !hasValue(parsedData.vatRates)) {
                parsedData.vatRates = EU_DEFAULT_TAX_RATE;
            }

            const calculatedExpenseData = applyEuCalculation(
                parsedData,
                {},
                "vatRates",
                (value) => value
            );

            setExpenseData(calculatedExpenseData);
            setSnack({open: true, msg: "Belegdaten ausgelesen. Bitte prüfen.", sev: "info"});
        } catch (err) {
            logError("Fehler beim Auslesen der Ausgabe", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Fehler beim Auslesen des Belegs", sev: "error"});
            setExpenseData(
                applyEuCalculation(
                    {
                        taxExcludedWithdrawal: false,
                        isEuService: false,
                        invoiceNumber: hasValue(fileDisplayName) ? fileDisplayName : null,
                    },
                    {},
                    "vatRates",
                    (value) => value
                )
            );
        } finally {
            setParsing(false);
        }
    };

    const handleSave = async () => {
        if (!expenseData) return;
        setExpenseErrors({});
        if (!validateEuService(expenseData, setExpenseErrors)) return;
        try {
            setSaving(true);
            const payload = {
                ...expenseData,
                taxExcludedWithdrawal: Boolean(expenseData?.taxExcludedWithdrawal),
                isEuService: Boolean(expenseData?.isEuService),
            };
            let savedExpense = null;
            if (manual) {
                const response = await createExpense(payload);
                savedExpense = response?.data || null;
            } else {
                if (!file) return;
                const formData = new FormData();
                formData.append("file", file);
                const uploaded = await uploadExpense(formData);
                const updated = await updateExpense(uploaded.data.id, payload);
                savedExpense = {...uploaded.data, ...(updated?.data || {})};
            }
            if (savedExpense) {
                upsertExpense(savedExpense);
                refresh({silent: true});
            }
            setSnack({open: true, msg: "Ausgabe gespeichert", sev: "success"});
            handleCloseDialog();
        } catch (err) {
            logError("Fehler beim Speichern der Ausgabe", err, {component: "ExpenseManagement"});
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyExpenseErrors(details, setExpenseErrors);
            }
            setSnack({open: true, msg: "Fehler beim Speichern der Ausgabe", sev: "error"});
        } finally {
            setSaving(false);
        }
    };

    const updateRecurringForm = (field, value) => {
        setRecurringForm((prev) => ({...(prev || {}), [field]: value}));
        clearRecurringError(field);
    };

    const handleOpenRecurringCreate = () => {
        const today = new Date();
        const start = toDateInput(today);
        setRecurringMode("create");
        setRecurringErrors({});
        setRecurringForm({
            id: null,
            title: "",
            counterparty: "",
            category: "",
            invoiceNumberTemplate: "",
            grossAmount: "",
            netAmount: "",
            vatAmount: "",
            recurrencePeriod: "MONTHLY",
            dayOfMonth: String(today.getDate()),
            startDate: start,
            endDate: "",
            nextExecutionDate: "",
        });
        setRecurringDialogOpen(true);
    };

    const handleOpenRecurringEdit = (transaction) => {
        if (!transaction) return;
        setRecurringMode("edit");
        setRecurringErrors({});
        setRecurringForm({
            id: transaction.id,
            title: transaction.title || "",
            counterparty: transaction.counterparty || "",
            category: transaction.category || "",
            invoiceNumberTemplate: transaction.invoiceNumberTemplate || "",
            grossAmount: toEditableNumber(transaction.grossAmount),
            netAmount: toEditableNumber(transaction.netAmount),
            vatAmount: toEditableNumber(transaction.vatAmount),
            recurrencePeriod: transaction.recurrencePeriod || "MONTHLY",
            dayOfMonth:
                transaction.dayOfMonth !== null && transaction.dayOfMonth !== undefined
                    ? String(transaction.dayOfMonth)
                    : "",
            startDate: toDateInput(transaction.startDate),
            endDate: toDateInput(transaction.endDate),
            nextExecutionDate: toDateInput(transaction.nextExecutionDate),
        });
        setRecurringDialogOpen(true);
    };

    const handleCloseRecurringDialog = () => {
        setRecurringDialogOpen(false);
        setRecurringForm(null);
        setRecurringMode("create");
        setRecurringSaving(false);
        setRecurringErrors({});
    };

    const handleSaveRecurring = async () => {
        if (!recurringForm) return;
        setRecurringErrors({});
        const payload = {
            id: recurringForm.id,
            transactionType: "EXPENSE",
            title: emptyToNull(recurringForm.title),
            counterparty: emptyToNull(recurringForm.counterparty),
            category: emptyToNull(recurringForm.category),
            invoiceNumberTemplate: emptyToNull(recurringForm.invoiceNumberTemplate),
            recurrencePeriod: recurringForm.recurrencePeriod || "MONTHLY",
            dayOfMonth: parseDayOfMonth(recurringForm.dayOfMonth),
            startDate: emptyToNull(recurringForm.startDate),
            endDate: emptyToNull(recurringForm.endDate),
            nextExecutionDate: emptyToNull(recurringForm.nextExecutionDate),
            grossAmount: parseNumberInput(recurringForm.grossAmount),
            netAmount: parseNumberInput(recurringForm.netAmount),
            vatAmount: parseNumberInput(recurringForm.vatAmount),
        };

        setRecurringSaving(true);
        try {
            if (recurringMode === "edit" && payload.id) {
                await updateRecurringTransaction(payload.id, payload);
                setSnack({open: true, msg: "Wiederkehrende Ausgabe aktualisiert", sev: "success"});
            } else {
                const {id: _id, ...createPayload} = payload;
                await createRecurringTransaction(createPayload);
                setSnack({open: true, msg: "Wiederkehrende Ausgabe gespeichert", sev: "success"});
            }
            await refreshRecurring();
            await refresh();
            handleCloseRecurringDialog();
        } catch (err) {
            logError("Fehler beim Speichern der wiederkehrenden Ausgabe", err, {component: "ExpenseManagement"});
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyRecurringErrors(details);
            }
            setSnack({open: true, msg: "Wiederkehrende Ausgabe konnte nicht gespeichert werden", sev: "error"});
        } finally {
            setRecurringSaving(false);
        }
    };

    const handleDeleteRecurring = async (transaction) => {
        if (!transaction?.id) return;
        const confirmed = window.confirm(
            `Wiederkehrende Ausgabe "${transaction.title || "Vorlage"}" wirklich löschen?`
        );
        if (!confirmed) return;
        setRecurringDeleteId(transaction.id);
        try {
            await deleteRecurringTransaction(transaction.id);
            await refreshRecurring();
            await refresh();
            setSnack({open: true, msg: "Wiederkehrende Ausgabe gelöscht", sev: "success"});
        } catch (err) {
            logError("Fehler beim Löschen der wiederkehrenden Ausgabe", err, {component: "ExpenseManagement"});
            setSnack({open: true, msg: "Wiederkehrende Ausgabe konnte nicht gelöscht werden", sev: "error"});
        } finally {
            setRecurringDeleteId(null);
        }
    };

    if (!hasAccounting) {
        return (
            <Box
                sx={{
                    py: {xs: 6, md: 10},
                    px: 2,
                    background: "linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)",
                    minHeight: "100vh",
                }}
            >
                <Container maxWidth="md">
                    <Stack spacing={2} alignItems="flex-start">
                        <Typography variant="h4" fontWeight={800}>
                            Ausgabenverwaltung
                        </Typography>
                        <Typography color="text.secondary">
                            Beleg-Upload, OCR und Auswertungen sind Teil des Buchhaltungstarifs.
                        </Typography>
                        <UpgradeNotice
                            title="Ausgabenverwaltung freischalten"
                            description="Schalte den 20 € netto Tarif frei, um Ausgaben digital zu erfassen und auszuwerten."
                            actionLabel="Zum Buchhaltungstarif"
                        />
                    </Stack>
                </Container>
            </Box>
        );
    }

    /* ===== Render ===== */
    return (
        <>
            {/* Full-bleed Hero */}
            <Box
                sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)",
                    py: {xs: 3, md: 4},
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        direction={{xs: "column", md: "row"}}
                        spacing={2}
                        alignItems={{xs: "flex-start", md: "center"}}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h4" sx={{fontWeight: 900, letterSpacing: 0.2}}>
                                Ausgaben
                            </Typography>
                            <Typography variant="subtitle1" sx={{mt: 0.5}}>
                                Belege hochladen oder manuell erfassen – mit Kategorien & Beträgen.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshRoundedIcon/>}
                                onClick={() => {
                                    Promise.all([refresh(), refreshRecurring(), loadSuppliers()]);
                                    setSnack({open: true, msg: "Aktualisiert.", sev: "success"});
                                }}
                            >
                                Aktualisieren
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddRoundedIcon/>}
                                onClick={handleOpenDialog}
                                sx={{
                                    backgroundColor: "orange",
                                    color: "#000",
                                    fontWeight: 800,
                                    "&:hover": {backgroundColor: "#ff9e1a"},
                                }}
                            >
                                Ausgabe erfassen
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Wiederkehrende Ausgaben */}
            <Container maxWidth="lg" sx={{mt: 3}}>
                <RecurringExpenseSection
                    recurringLoading={recurringLoading}
                    recurringError={recurringError}
                    recurringExpenses={recurringExpenses}
                    handleOpenRecurringCreate={handleOpenRecurringCreate}
                    handleOpenRecurringEdit={handleOpenRecurringEdit}
                    handleDeleteRecurring={handleDeleteRecurring}
                    recurringDeleteId={recurringDeleteId}
                />
            </Container>

            {/* Inhalt */}
            <Container maxWidth="lg" sx={{mt: 4, pb: 6}}>
                <ExpenseList
                    loading={loading}
                    expenseMonthIndexLoaded={expenseMonthIndexLoaded}
                    hasExpenseMonths={hasExpenseMonths}
                    hasUnknownExpenses={hasUnknownExpenses}
                    handleOpenDialog={handleOpenDialog}
                    sortedYears={sortedYears}
                    groupedExpenseMonths={groupedExpenseMonths}
                    expandedExpenseGroups={expandedExpenseGroups}
                    toggleYearExpanded={toggleYearExpanded}
                    toggleMonthExpanded={toggleMonthExpanded}
                    expenseMonthData={expenseMonthData}
                    loadingExpenseMonths={loadingExpenseMonths}
                    loadedExpenseMonths={loadedExpenseMonths}
                    handleOpenDetails={handleOpenDetails}
                    handleCopyExpense={handleCopyExpense}
                    openReceipt={openReceipt}
                    hasReceipt={hasReceipt}
                    shouldSuggestAsset={shouldSuggestAsset}
                    handleSuggestAsset={handleSuggestAsset}
                    copyingId={copyingId}
                />
            </Container>

            {/* Wiederkehrende Ausgabe bearbeiten/anlegen */}
            <Dialog open={recurringDialogOpen} onClose={handleCloseRecurringDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {recurringMode === "edit"
                        ? "Wiederkehrende Ausgabe bearbeiten"
                        : "Wiederkehrende Ausgabe anlegen"}
                </DialogTitle>
                <DialogContent dividers>
                    {recurringForm ? (
                        <Stack spacing={2}>
                            <TextField
                                label="Titel"
                                value={recurringForm.title || ""}
                                onChange={(e) => updateRecurringForm("title", e.target.value)}
                                fullWidth
                                autoFocus
                                error={Boolean(recurringErrors.title)}
                                helperText={recurringErrors.title || ""}
                            />
                            <TextField
                                label="Lieferant"
                                value={recurringForm.counterparty || ""}
                                onChange={(e) => updateRecurringForm("counterparty", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.counterparty)}
                                helperText={recurringErrors.counterparty || ""}
                            />
                            <Grid2 container spacing={2}>
                                <Grid2 item xs={12} sm={6}>
                                    <FormControl fullWidth error={Boolean(recurringErrors.recurrencePeriod)}>
                                        <InputLabel id="recurrence-period-label">Rhythmus</InputLabel>
                                        <Select
                                            labelId="recurrence-period-label"
                                            id="recurrence-period-select"
                                            label="Rhythmus"
                                            value={recurringForm.recurrencePeriod || 'MONTHLY'}
                                            onChange={(e) => updateRecurringForm('recurrencePeriod', e.target.value)}
                                        >
                                            {recurrenceOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {recurringErrors.recurrencePeriod && (
                                            <FormHelperText>{recurringErrors.recurrencePeriod}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid2>
                                <Grid2 item xs={12} sm={6}>
                                    <TextField
                                                                            label="Tag im Monat"
                                                                            type="number"
                                                                            inputMode="decimal"
                                                                            inputProps={{ min: "1", max: "31", step: "1" }}
                                                                            value={recurringForm.dayOfMonth ?? ""}
                                                                            onChange={(e) => updateRecurringForm("dayOfMonth", e.target.value)}
                                                                            fullWidth
                                                                            error={Boolean(recurringErrors.dayOfMonth)}
                                                                            helperText={recurringErrors.dayOfMonth || "1–31, leer = Startdatum"}
                                                                        />
                                </Grid2>
                                <Grid2 item xs={12} sm={6}>
                                    <AppDatePicker
                                        label="Startdatum"
                                        value={recurringForm.startDate || ""}
                                        onChange={(date) => updateRecurringForm("startDate", date)}
                                        fullWidth
                                        error={Boolean(recurringErrors.startDate)}
                                        helperText={recurringErrors.startDate || ""}
                                    />
                                </Grid2>
                                <Grid2 item xs={12} sm={6}>
                                    <AppDatePicker
                                        label="Enddatum"
                                        value={recurringForm.endDate || ""}
                                        onChange={(date) => updateRecurringForm("endDate", date)}
                                        fullWidth
                                        error={Boolean(recurringErrors.endDate)}
                                        helperText={recurringErrors.endDate || "Optional"}
                                    />
                                </Grid2>
                            </Grid2>
                            <TextField
                                select
                                label="Kategorie"
                                value={recurringForm.category || ""}
                                onChange={(e) => updateRecurringForm("category", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.category)}
                                helperText={recurringErrors.category || ""}
                            >
                                <MenuItem value="">Keine Angabe</MenuItem>
                                {EXPENSE_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Grid2 container spacing={2}>
                                                            <Grid2 item xs={12} sm={4}>
                                                                <TextField
                                                                    label="Betrag brutto"
                                                                    type="number"
                                                                    inputMode="decimal"
                                                                    inputProps={{ step: "0.01", min: "0" }}
                                                                    value={recurringForm.grossAmount ?? ""}
                                                                    onChange={(e) => updateRecurringForm("grossAmount", e.target.value)}
                                                                    fullWidth
                                                                    error={Boolean(recurringErrors.grossAmount)}
                                                                    helperText={recurringErrors.grossAmount || ""}
                                                                />
                                                            </Grid2>
                                                            <Grid2 item xs={12} sm={4}>
                                                                <TextField
                                                                    label="Betrag netto"
                                                                    type="number"
                                                                    inputMode="decimal"
                                                                    inputProps={{ step: "0.01", min: "0" }}
                                                                    value={recurringForm.netAmount ?? ""}
                                                                    onChange={(e) => updateRecurringForm("netAmount", e.target.value)}
                                                                    fullWidth
                                                                    error={Boolean(recurringErrors.netAmount)}
                                                                    helperText={recurringErrors.netAmount || ""}
                                                                />
                                                            </Grid2>
                                                            <Grid2 item xs={12} sm={4}>
                                                                <TextField
                                                                    label="USt-Betrag"
                                                                    type="number"
                                                                    inputMode="decimal"
                                                                    inputProps={{ step: "0.01", min: "0" }}
                                                                    value={recurringForm.vatAmount ?? ""}
                                                                    onChange={(e) => updateRecurringForm("vatAmount", e.target.value)}
                                                                    fullWidth
                                                                    error={Boolean(recurringErrors.vatAmount)}
                                                                    helperText={recurringErrors.vatAmount || ""}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                            <TextField
                                label="Belegnr.-Muster"
                                value={recurringForm.invoiceNumberTemplate || ""}
                                onChange={(e) => updateRecurringForm("invoiceNumberTemplate", e.target.value)}
                                fullWidth
                                error={Boolean(recurringErrors.invoiceNumberTemplate)}
                                helperText={
                                    recurringErrors.invoiceNumberTemplate ||
                                    "Nutze %s als Platzhalter, z. B. ABR-%s"
                                }
                            />
                            <AppDatePicker
                                label="Nächste Ausführung"
                                value={recurringForm.nextExecutionDate || ""}
                                onChange={(date) => updateRecurringForm("nextExecutionDate", date)}
                                fullWidth
                                error={Boolean(recurringErrors.nextExecutionDate)}
                                helperText={
                                    recurringErrors.nextExecutionDate ||
                                    "Optional – leer lassen für automatische Berechnung"
                                }
                            />
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Formular konnte nicht geladen werden.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRecurringDialog} disabled={recurringSaving}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleSaveRecurring}
                        disabled={recurringSaving}
                        variant="contained"
                        sx={{
                            backgroundColor: "orange",
                            color: "#000",
                            fontWeight: 700,
                            "&:hover": {backgroundColor: "#ff9e1a"}
                        }}
                    >
                        {recurringSaving ? "Speichere…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Ausgabe erfassen */}
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle>Ausgabe erfassen</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={manual}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setManual(checked);
                                        setExpenseErrors({});
                                        if (checked) {
                                            setExpenseData((prev) => {
                                                let newExpenseData = prev || {
                                                    taxExcludedWithdrawal: false,
                                                    isEuService: false
                                                };
                                                if (newExpenseData.isEuService && !hasValue(newExpenseData.vatRates)) {
                                                    newExpenseData.vatRates = EU_DEFAULT_TAX_RATE;
                                                }
                                                return applyEuCalculation(
                                                    newExpenseData,
                                                    {},
                                                    "vatRates",
                                                    (value) => value
                                                );
                                            });
                                            setFile(null);
                                        } else {
                                            setExpenseData(null);
                                        }
                                    }}
                                />}
                            label="Manuell erfassen (ohne Datei)"
                        />

                        {!manual && !expenseData && (
                            <>
                                <Box
                                    ref={dropRef}
                                    sx={{
                                        border: "2px dashed",
                                        borderColor: "divider",
                                        borderRadius: 3,
                                        p: 4,
                                        textAlign: "center",
                                        transition: "border-color .12s ease, background-color .12s ease",
                                        "&.drag-active": {
                                            borderColor: "orange",
                                            backgroundColor: "rgba(255,165,0,0.06)",
                                        },
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <CloudUploadRoundedIcon fontSize="large"/>
                                    <Typography variant="h6" sx={{mt: 1, fontWeight: 800}}>
                                        Datei hierher ziehen
                                    </Typography>
                                    <Typography variant="body2" sx={{mt: 0.5, opacity: 0.8}}>
                                        oder <b>Klicken</b>, um eine Datei auszuwählen
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{display: "none"}}
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    />
                                </Box>

                                {file && (
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <InsertDriveFileRoundedIcon/>
                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Typography variant="subtitle2" noWrap sx={{fontWeight: 700}}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{opacity: 0.75}}>
                                                    {formatBytes(file.size)}
                                                </Typography>
                                            </Box>
                                            <Chip size="small" label={extOf(file.name)}/>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setPreviewVisible((prev) => !prev)}
                                                disabled={!file}
                                                sx={{whiteSpace: "nowrap"}}
                                            >
                                                {previewVisible ? "Beleg ausblenden" : "Beleg anzeigen"}
                                            </Button>
                                            <IconButton onClick={() => setFile(null)} disabled={parsing}>
                                                <CloseRoundedIcon/>
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                )}
                            </>
                        )}

                        {file && !manual && (
                            <Collapse in={previewVisible}>
                                <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                            Belegvorschau
                                        </Typography>
                                        <Typography variant="body2" sx={{opacity: 0.75}}>
                                            {file.name} · {formatBytes(file.size)}
                                        </Typography>
                                        {previewUrl ? (
                                            <>
                                                {isPreviewPdf && (
                                                    <Box sx={{height: {xs: "50vh", md: "60vh"}}}>
                                                        <iframe
                                                            title="Belegvorschau"
                                                            src={previewUrl}
                                                            style={{width: "100%", height: "100%", border: "none"}}
                                                        />
                                                    </Box>
                                                )}
                                                {isPreviewImage && (
                                                    <Box sx={{display: "flex", justifyContent: "center"}}>
                                                        <Box
                                                            component="img"
                                                            src={previewUrl}
                                                            alt="Belegvorschau"
                                                            sx={{maxWidth: "100%", maxHeight: "60vh", borderRadius: 2}}
                                                        />
                                                    </Box>
                                                )}
                                                {!isPreviewPdf && !isPreviewImage && (
                                                    <Alert severity="info" variant="outlined">
                                                        Vorschau fuer diesen Dateityp ist nicht verfuegbar.
                                                    </Alert>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Vorschau wird geladen.
                                            </Typography>
                                        )}
                                    </Stack>
                                </Paper>
                            </Collapse>
                        )}

                        {(manual || expenseData) && (
                            <>
                                <Stack spacing={3}>
                                    {file && !manual && (
                                        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 2}}>
                                            <Stack
                                                direction={{xs: "column", sm: "row"}}
                                                spacing={1.5}
                                                alignItems={{xs: "flex-start", sm: "center"}}
                                                justifyContent="space-between"
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center"
                                                       sx={{minWidth: 0}}>
                                                    <InsertDriveFileRoundedIcon fontSize="small"/>
                                                    <Typography variant="body2" noWrap sx={{fontWeight: 600}}>
                                                        {file.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{opacity: 0.7}}>
                                                        {formatBytes(file.size)}
                                                    </Typography>
                                                </Stack>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setPreviewVisible((prev) => !prev)}
                                                    disabled={!file}
                                                    sx={{whiteSpace: "nowrap"}}
                                                >
                                                    {previewVisible ? "Beleg ausblenden" : "Beleg anzeigen"}
                                                </Button>
                                            </Stack>
                                        </Paper>
                                    )}
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                            Belegdaten
                                        </Typography>
                                        <Grid2 container spacing={2}>
                                            <Grid2 xs={12} sm={6}>
                                                <AppDatePicker
                                                    label="Belegdatum"
                                                    value={toDateInput(expenseData?.invoiceDate)}
                                                    onChange={(date) => {
                                                        updateExpenseData({invoiceDate: date});
                                                        clearExpenseError("invoiceDate");
                                                    }}
                                                    error={Boolean(expenseErrors.invoiceDate)}
                                                    helperText={expenseErrors.invoiceDate || ""}
                                                />
                                            </Grid2>
                                            <Grid2 xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(expenseData?.isEuService)}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                updateExpenseDataWithEu((current) => ({
                                                                    isEuService: checked,
                                                                    vatRates: checked && !hasValue(current.vatRates)
                                                                        ? EU_DEFAULT_TAX_RATE
                                                                        : current.vatRates,
                                                                }), "isEuService");
                                                                clearExpenseError("vatRates");
                                                                clearExpenseError("netAmount");
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <span>Leistung aus der EU / EU-Ausland (Reverse Charge)</span>
                                                            <ContextTooltip
                                                                title="EU-Leistung"
                                                                description="Bei EU-Leistungen wird die Ausgabe netto als Betriebsausgabe erfasst. USt/Vorsteuer werden in der USt-Auswertung beruecksichtigt."
                                                            />
                                                        </Stack>
                                                    }
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <AppDatePicker
                                                    label="gezahlt am"
                                                    value={toDateInput(expenseData?.paymentDueDate)}
                                                    onChange={(date) => {
                                                        updateExpenseData({paymentDueDate: date});
                                                        clearExpenseError("paymentDueDate");
                                                    }}
                                                    error={Boolean(expenseErrors.paymentDueDate)}
                                                    helperText={expenseErrors.paymentDueDate || ""}
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    label="Belegnummer/Anzeigename"
                                                    value={expenseData?.invoiceNumber || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({invoiceNumber: e.target.value});
                                                        clearExpenseError("invoiceNumber");
                                                    }}
                                                    error={Boolean(expenseErrors.invoiceNumber)}
                                                    helperText={expenseErrors.invoiceNumber || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    label="Zahlungsart"
                                                    value={expenseData?.paymentMethod || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({paymentMethod: e.target.value});
                                                        clearExpenseError("paymentMethod");
                                                    }}
                                                    error={Boolean(expenseErrors.paymentMethod)}
                                                    helperText={expenseErrors.paymentMethod || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    select
                                                    label="Belegkategorie"
                                                    value={expenseData?.category || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({category: e.target.value});
                                                        clearExpenseError("category");
                                                    }}
                                                    error={Boolean(expenseErrors.category)}
                                                    helperText={expenseErrors.category || ""}
                                                    sx={{minWidth: 220}}
                                                    fullWidth
                                                >
                                                    {EXPENSE_CATEGORIES.map((c) => (
                                                        <MenuItem key={c} value={c}>
                                                            {c}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    label="Rechnungsart"
                                                    value={expenseData?.invoiceType || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({invoiceType: e.target.value});
                                                        clearExpenseError("invoiceType");
                                                    }}
                                                    error={Boolean(expenseErrors.invoiceType)}
                                                    helperText={expenseErrors.invoiceType || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </Stack>

                                    <Divider/>

                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                            Lieferant & Bank
                                        </Typography>
                                        <Grid2 container spacing={2}>
                                            <Grid2 xs={12} sm={12}>
                                                <Autocomplete
                                                    options={supplierOptions}
                                                    value={resolveSupplierOption(
                                                        supplierOptions,
                                                        expenseData?.supplierId,
                                                        expenseData?.supplierId ? expenseData?.supplierName : ""
                                                    )}
                                                    inputValue={expenseData?.supplierName || ""}
                                                    onChange={(event, value) => handleExpenseSupplierSelection(value)}
                                                    onInputChange={(event, value, reason) =>
                                                        handleExpenseSupplierInput(value, reason)
                                                    }
                                                    getOptionLabel={getSupplierOptionLabel}
                                                    isOptionEqualToValue={isSupplierOptionEqualToValue}
                                                    loading={suppliersLoading}
                                                    loadingText={SUPPLIER_SELECT_LOADING}
                                                    noOptionsText={SUPPLIER_SELECT_NO_OPTIONS}
                                                    sx={{minWidth: 300}}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={SUPPLIER_SELECT_LABEL}
                                                            placeholder={SUPPLIER_SELECT_PLACEHOLDER}
                                                            error={Boolean(expenseErrors.supplierId)}
                                                            helperText={
                                                                expenseErrors.supplierId ||
                                                                (!suppliersLoading && supplierOptions.length === 0
                                                                    ? SUPPLIER_SELECT_EMPTY_HELPER
                                                                    : "")
                                                            }
                                                            sx={{minWidth: 300}}
                                                            fullWidth
                                                        />
                                                    )}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={4}>
                                                <TextField
                                                    label="IBAN"
                                                    value={expenseData?.iban || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({iban: e.target.value});
                                                        clearExpenseError("iban");
                                                    }}
                                                    error={Boolean(expenseErrors.iban)}
                                                    helperText={expenseErrors.iban || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12}>
                                                <MultilineTextField
                                                    label="Anschrift"
                                                    value={expenseData?.supplierAddress || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({supplierAddress: e.target.value});
                                                        clearExpenseError("supplierAddress");
                                                    }}
                                                    error={Boolean(expenseErrors.supplierAddress)}
                                                    helperText={expenseErrors.supplierAddress || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            {expenseData?.supplierName &&
                                                !expenseData?.supplierId &&
                                                !suppliersLoading &&
                                                !buildSupplierValue(expenseData.supplierId, expenseData.supplierName) && (
                                                    <Grid2 xs={12}>
                                                        <Alert
                                                            severity="info"
                                                            variant="outlined"
                                                            action={
                                                                <Button
                                                                    size="small"
                                                                    onClick={handleCreateSupplierFromExpense}
                                                                    disabled={supplierCreating}
                                                                >
                                                                    Lieferant anlegen
                                                                </Button>
                                                            }
                                                        >
                                                            Kein Lieferant mit dem
                                                            Namen <strong>{expenseData.supplierName}</strong> gefunden.
                                                        </Alert>
                                                    </Grid2>
                                                )}
                                        </Grid2>
                                    </Stack>

                                    <Divider/>

                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                            Beträge & Steuer
                                        </Typography>
                                        <Grid2 container spacing={2}>
                                            <Grid2 xs={12} sm={4}>
                                                <TextField
                                                    label="Betrag brutto"
                                                    type="number"
                                                    value={expenseData?.grossAmount ?? ""}
                                                    onChange={(e) => {
                                                        updateExpenseDataWithEu(
                                                            {grossAmount: parseFloat(e.target.value) || 0},
                                                            "grossAmount"
                                                        );
                                                        clearExpenseError("grossAmount");
                                                        clearExpenseError("netAmount");
                                                    }}
                                                    error={Boolean(expenseErrors.grossAmount)}
                                                    helperText={expenseErrors.grossAmount || ""}
                                                    fullWidth
                                                    inputProps={{step: "0.01", min: "0"}}
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={4}>
                                                <TextField
                                                    label="Betrag netto"
                                                    type="number"
                                                    value={expenseData?.netAmount ?? ""}
                                                    onChange={(e) => {
                                                        updateExpenseDataWithEu(
                                                            {netAmount: parseFloat(e.target.value) || 0},
                                                            "netAmount"
                                                        );
                                                        clearExpenseError("netAmount");
                                                    }}
                                                    error={Boolean(expenseErrors.netAmount)}
                                                    helperText={expenseErrors.netAmount || ""}
                                                    fullWidth
                                                    inputProps={{step: "0.01", min: "0"}}
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={4}>
                                                <TextField
                                                    label="USt-Betrag"
                                                    type="number"
                                                    value={expenseData?.vatAmount ?? ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({vatAmount: parseFloat(e.target.value) || 0});
                                                        clearExpenseError("vatAmount");
                                                        clearExpenseError("netAmount");
                                                    }}
                                                    error={Boolean(expenseErrors.vatAmount)}
                                                    helperText={expenseErrors.vatAmount || ""}
                                                    fullWidth
                                                    inputProps={{step: "0.01", min: "0"}}
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    label="USt-Sätze (Text)"
                                                    value={expenseData?.vatRates || ""}
                                                    onChange={(e) => {
                                                        updateExpenseDataWithEu({vatRates: e.target.value}, "vatRates");
                                                        clearExpenseError("vatRates");
                                                        clearExpenseError("netAmount");
                                                    }}
                                                    error={Boolean(expenseErrors.vatRates)}
                                                    helperText={expenseErrors.vatRates || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12} sm={6}>
                                                <TextField
                                                    label="Vorschau Gesamt (brutto)"
                                                    value={fmtEUR(expenseData?.grossAmount || 0)}
                                                    fullWidth
                                                    disabled
                                                />
                                            </Grid2>
                                            <Grid2 xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(expenseData?.taxExcludedWithdrawal)}
                                                            onChange={(e) =>
                                                                updateExpenseData({
                                                                    taxExcludedWithdrawal: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                    }
                                                    label="Als steuerlich nicht zu berücksichtigende Entnahme behandeln"
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </Stack>

                                    <Divider/>

                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                            Leistungsdetails
                                        </Typography>
                                        <Grid2 container spacing={2}>
                                            <Grid2 xs={12}>
                                                <TextField
                                                    label="Leistungszeitraum"
                                                    value={expenseData?.servicePeriod || ""}
                                                    onChange={(e) => {
                                                        updateExpenseData({servicePeriod: e.target.value});
                                                        clearExpenseError("servicePeriod");
                                                    }}
                                                    error={Boolean(expenseErrors.servicePeriod)}
                                                    helperText={expenseErrors.servicePeriod || ""}
                                                    fullWidth
                                                />
                                            </Grid2>
                                            <Grid2 xs={12}>
                                                                                            <MultilineTextField
                                                                                                label="Positionsdaten"
                                                                                                value={expenseData?.positionsData || ""}
                                                                                                onChange={(e) => {
                                                                                                    updateExpenseData({positionsData: e.target.value});
                                                                                                    clearExpenseError("positionsData");
                                                                                                }}
                                                                                                error={Boolean(expenseErrors.positionsData)}
                                                                                                helperText={expenseErrors.positionsData || ""}
                                                                                                fullWidth
                                                                                            />                                            </Grid2>
                                        </Grid2>
                                    </Stack>
                                </Stack>
                            </>
                        )}

                        {!manual && !expenseData && (
                            <Alert severity="info" variant="outlined">
                                Lade eine PDF/JPG/PNG hoch und lasse die Felder automatisch erkennen – oder aktiviere
                                „Manuell erfassen“.
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    {!manual && !expenseData ? (
                        <>
                            <Button onClick={handleCloseDialog} color="inherit">
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleUploadParse}
                                disabled={!file || parsing}
                                variant="contained"
                                sx={{
                                    bgcolor: "orange",
                                    "&:hover": {bgcolor: "#cc7a00"},
                                    borderRadius: 2,
                                    color: "#000"
                                }}
                            >
                                {parsing ? "Lese aus…" : "Hochladen & auslesen"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleCloseDialog} color="inherit">
                                Schließen
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                variant="contained"
                                sx={{
                                    bgcolor: "orange",
                                    "&:hover": {bgcolor: "#cc7a00"},
                                    borderRadius: 2,
                                    color: "#000"
                                }}
                            >
                                {saving ? "Speichere…" : "Speichern"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={detailOpen}
                onClose={handleCloseDetails}
                fullWidth
                maxWidth="md"
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle>Ausgabe – Details</DialogTitle>
                <DialogContent dividers>
                    {detailExpense && detailData ? (
                        <Stack spacing={2}>
                            <Typography variant="body2" sx={{opacity: 0.8}}>
                                <strong>Datei:</strong> {detailExpense.originalFilename || "—"}
                            </Typography>
                            {detailReceiptProcessingStatus === RECEIPT_PROCESSING_STATUS.PROCESSING && (
                                <Alert severity="info" variant="outlined">
                                    Beleg wird verarbeitet. Die Angaben stehen gleich zur Verfuegung.
                                </Alert>
                            )}
                            {detailReceiptProcessingStatus === RECEIPT_PROCESSING_STATUS.FAILED && (
                                <Alert
                                    severity="warning"
                                    variant="outlined"
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => startReceiptProcessing(detailExpense.id, {force: true})}
                                        >
                                            Erneut versuchen
                                        </Button>
                                    }
                                >
                                    {detailReceiptProcessingError
                                        ? `Beleg konnte nicht verarbeitet werden: ${detailReceiptProcessingError}`
                                        : "Beleg konnte nicht verarbeitet werden."}
                                </Alert>
                            )}
                            <Button
                                component="label"
                                role={undefined}
                                variant="outlined"
                                tabIndex={-1}
                                startIcon={<CloudUploadRoundedIcon/>}
                                disabled={detailSaving}
                            >
                                Neuen Beleg hochladen
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
                                    accept="application/pdf,image/png,image/jpeg,image/gif"
                                />
                            </Button>
                            {receiptFile && (
                                <Chip
                                    icon={<InsertDriveFileRoundedIcon/>}
                                    label={receiptFile.name}
                                    onDelete={() => setReceiptFile(null)}
                                />
                            )}
                            <Typography variant="body2" sx={{opacity: 0.8}}>
                                <strong>Hochgeladen:</strong>{" "}
                                {detailExpense.uploadDate
                                    ? new Date(detailExpense.uploadDate).toLocaleDateString("de-DE")
                                    : "-"}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(detailData.isEuService)}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            updateDetailDataWithEu((current) => ({
                                                isEuService: checked,
                                                vatRates: checked && !hasValue(current.vatRates)
                                                    ? EU_DEFAULT_TAX_RATE
                                                    : current.vatRates,
                                            }), "isEuService");
                                            clearDetailError("vatRates");
                                            clearDetailError("netAmount");
                                        }}
                                    />
                                }
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <span>Leistung aus der EU / EU-Ausland (Reverse Charge)</span>
                                        <ContextTooltip
                                            title="EU-Leistung"
                                            description="Bei EU-Leistungen wird die Ausgabe netto als Betriebsausgabe erfasst. USt/Vorsteuer werden in der USt-Auswertung beruecksichtigt."
                                        />
                                    </Stack>
                                }
                            />
                            {detailData.isEuService && (
                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                    <strong>Netto
                                        verbucht:</strong> {fmtEUR(parseNumberInput(detailData.netAmount) ?? 0)}
                                </Typography>
                            )}
                            <Stack spacing={3}>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                        Belegdaten
                                    </Typography>
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12} sm={6}>
                                            <AppDatePicker
                                                label="Rechnungsdatum"
                                                value={detailData.invoiceDate}
                                                onChange={(date) => updateDetail("invoiceDate", date)}
                                                disabled={detailSaving}
                                                error={Boolean(detailErrors.invoiceDate)}
                                                helperText={detailErrors.invoiceDate || ""}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <AppDatePicker
                                                label="gezahlt am"
                                                value={detailData.paymentDueDate || ""}
                                                onChange={(date) => updateDetail("paymentDueDate", date)}
                                                fullWidth
                                                error={Boolean(detailErrors.paymentDueDate)}
                                                helperText={detailErrors.paymentDueDate || ""}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                label="Belegnummer/Anzeigename"
                                                value={detailData.invoiceNumber || ""}
                                                onChange={(e) => updateDetail("invoiceNumber", e.target.value)}
                                                error={Boolean(detailErrors.invoiceNumber)}
                                                helperText={detailErrors.invoiceNumber || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                label="Zahlungsart"
                                                value={detailData.paymentMethod || ""}
                                                onChange={(e) => updateDetail("paymentMethod", e.target.value)}
                                                error={Boolean(detailErrors.paymentMethod)}
                                                helperText={detailErrors.paymentMethod || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                select
                                                label="Belegkategorie"
                                                value={detailData.category || ""}
                                                onChange={(e) => updateDetail("category", e.target.value)}
                                                error={Boolean(detailErrors.category)}
                                                helperText={detailErrors.category || ""}
                                                sx={{minWidth: 220}}
                                                fullWidth
                                            >
                                                {EXPENSE_CATEGORIES.map((c) => (
                                                    <MenuItem key={c} value={c}>
                                                        {c}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                label="Rechnungsart"
                                                value={detailData.invoiceType || ""}
                                                onChange={(e) => updateDetail("invoiceType", e.target.value)}
                                                error={Boolean(detailErrors.invoiceType)}
                                                helperText={detailErrors.invoiceType || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                    </Grid2>
                                </Stack>

                                <Divider/>

                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                        Lieferant & Bank
                                    </Typography>
                                    <Grid2 container rowSpacing={2} columnSpacing={4}>
                                        <Grid2 xs={12} sm="auto">
                                            <Autocomplete
                                                options={supplierOptions}
                                                value={resolveSupplierOption(
                                                    supplierOptions,
                                                    detailData.supplierId,
                                                    detailData.supplierId ? detailData.supplierName : ""
                                                )}
                                                inputValue={detailData.supplierName || ""}
                                                onChange={(event, value) => handleDetailSupplierSelection(value)}
                                                onInputChange={(event, value, reason) =>
                                                    handleDetailSupplierInput(value, reason)
                                                }
                                                getOptionLabel={getSupplierOptionLabel}
                                                isOptionEqualToValue={isSupplierOptionEqualToValue}
                                                loading={suppliersLoading}
                                                loadingText={SUPPLIER_SELECT_LOADING}
                                                noOptionsText={SUPPLIER_SELECT_NO_OPTIONS}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={SUPPLIER_SELECT_LABEL}
                                                        placeholder={SUPPLIER_SELECT_PLACEHOLDER}
                                                        error={Boolean(detailErrors.supplierId)}
                                                        helperText={
                                                            detailErrors.supplierId ||
                                                            (!suppliersLoading && supplierOptions.length === 0
                                                                ? SUPPLIER_SELECT_EMPTY_HELPER
                                                                : "")
                                                        }
                                                    />
                                                )}
                                                sx={{width: "fit-content", maxWidth: "100%"}}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={8}>
                                            <TextField
                                                label="IBAN"
                                                value={detailData.iban || ""}
                                                onChange={(e) => updateDetail("iban", e.target.value)}
                                                error={Boolean(detailErrors.iban)}
                                                helperText={detailErrors.iban || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={8}>
                                            <MultilineTextField
                                                label="Anschrift"
                                                value={detailData.supplierAddress || ""}
                                                onChange={(e) => updateDetail("supplierAddress", e.target.value)}
                                                error={Boolean(detailErrors.supplierAddress)}
                                                helperText={detailErrors.supplierAddress || ""}
                                                fullWidth
                                                minRows={2}
                                                maxRows={4}
                                            />
                                        </Grid2>
                                        {detailData.supplierName &&
                                            !detailData.supplierId &&
                                            !suppliersLoading &&
                                            !buildSupplierValue(detailData.supplierId, detailData.supplierName) && (
                                                <Grid2 xs={12}>
                                                    <Alert
                                                        severity="info"
                                                        variant="outlined"
                                                        action={
                                                            <Button
                                                                size="small"
                                                                onClick={handleCreateSupplierFromDetail}
                                                                disabled={supplierCreating}
                                                            >
                                                                Lieferant anlegen
                                                            </Button>
                                                        }
                                                    >
                                                        Kein Lieferant mit dem
                                                        Namen <strong>{detailData.supplierName}</strong> gefunden.
                                                    </Alert>
                                                </Grid2>
                                            )}
                                    </Grid2>
                                </Stack>

                                <Divider/>

                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                        Beträge & Steuer
                                    </Typography>
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12} sm={4}>
                                            <TextField
                                                label="Betrag brutto"
                                                type="number"
                                                value={detailData.grossAmount ?? ""}
                                                onChange={(e) =>
                                                    updateDetailDataWithEu(
                                                        {grossAmount: e.target.value},
                                                        "grossAmount"
                                                    )
                                                }
                                                error={Boolean(detailErrors.grossAmount)}
                                                helperText={detailErrors.grossAmount || ""}
                                                fullWidth
                                                inputProps={{step: "0.01", min: "0"}}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={4}>
                                            <TextField
                                                label="Betrag netto"
                                                type="number"
                                                value={detailData.netAmount ?? ""}
                                                onChange={(e) =>
                                                    updateDetailDataWithEu(
                                                        {netAmount: e.target.value},
                                                        "netAmount"
                                                    )
                                                }
                                                error={Boolean(detailErrors.netAmount)}
                                                helperText={detailErrors.netAmount || ""}
                                                fullWidth
                                                inputProps={{step: "0.01", min: "0"}}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={4}>
                                            <TextField
                                                label="USt-Betrag"
                                                type="number"
                                                value={detailData.vatAmount ?? ""}
                                                onChange={(e) => {
                                                    updateDetail("vatAmount", e.target.value);
                                                    clearDetailError("netAmount");
                                                }}
                                                error={Boolean(detailErrors.vatAmount)}
                                                helperText={detailErrors.vatAmount || ""}
                                                fullWidth
                                                inputProps={{step: "0.01", min: "0"}}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                label="USt-Sätze (Text)"
                                                value={detailData.vatRates || ""}
                                                onChange={(e) =>
                                                    updateDetailDataWithEu({vatRates: e.target.value}, "vatRates")
                                                }
                                                error={Boolean(detailErrors.vatRates)}
                                                helperText={detailErrors.vatRates || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <TextField
                                                label="Vorschau Gesamt (brutto)"
                                                value={fmtEUR(detailGrossPreview ?? 0)}
                                                fullWidth
                                                disabled
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(detailData.taxExcludedWithdrawal)}
                                                        onChange={(e) => updateDetail("taxExcludedWithdrawal", e.target.checked)}
                                                    />
                                                }
                                                label="Als steuerlich nicht zu berücksichtigende Entnahme behandeln"
                                            />
                                        </Grid2>
                                    </Grid2>
                                </Stack>

                                <Divider/>

                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                        Leistungsdetails
                                    </Typography>
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12}>
                                            <TextField
                                                label="Leistungszeitraum"
                                                value={detailData.servicePeriod || ""}
                                                onChange={(e) => updateDetail("servicePeriod", e.target.value)}
                                                error={Boolean(detailErrors.servicePeriod)}
                                                helperText={detailErrors.servicePeriod || ""}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <TextField
                                                label="Positionsdaten"
                                                value={detailData.positionsData || ""}
                                                onChange={(e) => updateDetail("positionsData", e.target.value)}
                                                error={Boolean(detailErrors.positionsData)}
                                                helperText={detailErrors.positionsData || ""}
                                                fullWidth
                                                multiline
                                                minRows={3}
                                            />
                                        </Grid2>
                                    </Grid2>
                                </Stack>
                            </Stack>

                        </Stack>
                    ) : (
                        <LinearProgress/>
                    )}
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={handleCloseDetails} color="inherit" disabled={detailSaving}>
                        Schließen
                    </Button>
                    <Button
                        onClick={handleDetailSave}
                        disabled={detailSaving}
                        variant="contained"
                        sx={{bgcolor: "orange", "&:hover": {bgcolor: "#cc7a00"}, borderRadius: 2, color: "#000"}}
                    >
                        {detailSaving ? "Speichere…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Belegvorschau */}
            <Dialog open={receiptOpen} onClose={closeReceipt} maxWidth="md" fullWidth>
                <DialogTitle>Belegvorschau</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                                {receiptExpense ? resolveExpenseDisplayName(receiptExpense) : "Beleg"}
                            </Typography>
                            {receiptExpense?.originalFilename && (
                                <Typography variant="body2" sx={{opacity: 0.7}}>
                                    Datei: {receiptExpense.originalFilename}
                                </Typography>
                            )}
                            {receiptPreview?.size ? (
                                <Typography variant="body2" sx={{opacity: 0.7}}>
                                    {formatBytes(receiptPreview.size)}
                                </Typography>
                            ) : null}
                        </Stack>

                        {receiptProcessingStatus === RECEIPT_PROCESSING_STATUS.PROCESSING && (
                            <Alert severity="info" variant="outlined">
                                Beleg wird verarbeitet. Die Angaben stehen gleich zur Verfuegung.
                            </Alert>
                        )}
                        {receiptProcessingStatus === RECEIPT_PROCESSING_STATUS.FAILED && (
                            <Alert
                                severity="warning"
                                variant="outlined"
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => startReceiptProcessing(receiptExpense?.id, {force: true})}
                                    >
                                        Erneut versuchen
                                    </Button>
                                }
                            >
                                {receiptProcessingError
                                    ? `Beleg konnte nicht verarbeitet werden: ${receiptProcessingError}`
                                    : "Beleg konnte nicht verarbeitet werden."}
                            </Alert>
                        )}

                        {receiptLoading && <LinearProgress/>}
                        {!receiptLoading && receiptError && (
                            <Alert severity="error" variant="outlined">
                                {receiptError}
                            </Alert>
                        )}
                        {!receiptLoading && !receiptError && receiptPreview?.url && (
                            <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                                        Belegvorschau
                                    </Typography>
                                    {isReceiptPdf && (
                                        <Box sx={{height: {xs: "50vh", md: "70vh"}}}>
                                            <iframe
                                                title="Belegvorschau"
                                                src={receiptPreview.url}
                                                style={{width: "100%", height: "100%", border: "none"}}
                                            />
                                        </Box>
                                    )}
                                    {isReceiptImage && (
                                        <Box sx={{display: "flex", justifyContent: "center"}}>
                                            <Box
                                                component="img"
                                                src={receiptPreview.url}
                                                alt="Belegvorschau"
                                                sx={{maxWidth: "100%", maxHeight: "70vh", borderRadius: 2}}
                                            />
                                        </Box>
                                    )}
                                    {!isReceiptPdf && !isReceiptImage && (
                                        <Alert severity="info" variant="outlined">
                                            Vorschau fuer diesen Dateityp ist nicht verfuegbar.
                                        </Alert>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                        {!receiptLoading && !receiptError && !receiptPreview?.url && (
                            <Typography variant="body2" color="text.secondary">
                                Keine Vorschau verfuegbar.
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReceipt}>Schliessen</Button>
                </DialogActions>
            </Dialog>

            <AssetFormDialog
                open={assetDialogOpen}
                title="Anlagegut anlegen"
                initialData={assetDraft}
                onClose={handleCloseAssetDialog}
                onSave={handleSaveAsset}
                saving={assetSaving}
                error={assetError}
            />

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({...s, open: false}))}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
            >
                <Alert
                    onClose={() => setSnack((s) => ({...s, open: false}))}
                    severity={snack.sev}
                    sx={{width: "100%"}}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </>
    );
}
